import { Grid, Paper, Skeleton } from "@mui/material";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getUsers } from "modules/agentMySecurity/services/embeddingsService";
import Users from "./users";
import { useEffect, useState } from "react";
import { useAppTheme } from "core/contexts/theme/theme";
import { CustomTooltip } from "../../home";
import {
  AbsoluteZIndexHeader,
  CardHeader,
  CustomPieText,
} from "../dashboard.styled";
import { ConversationAnalyticsTab } from "../conversations/home";

// User Analytics Tab
export const UserAnalyticsTab = () => {
  const { apptheme } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [newUsers, setNewUsers] = useState([]);

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      // In production, you would make actual API calls here
      // These would be the endpoints you need to create or already have
      try {
        // Fetch users
        const users = await fetchUsers();
        setUsers(users);

        // Fetch the list of API Keys
        const newUsers: any = fetchNewUsersPerDay(users);
        setNewUsers(newUsers);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <CustomPieText
        x={x}
        y={y}
        fill={apptheme.colors.textColor.slice(1)}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </CustomPieText>
    );
  };

  return (
    <Grid container spacing={3}>
      {/* New Users This Month */}
      <Grid item xs={12} md={9}>
        <Paper className="p-2">
          <CardHeader>New Users This Month</CardHeader>
          {loading ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={newUsers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ fill: apptheme.colors.textColor }} />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8884d8"
                  name="New Users"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* User Authentication Method */}
      <Grid item xs={12} md={3}>
        <Paper sx={{ p: 2, height: "100%" }} className="position-relative">
          <AbsoluteZIndexHeader>Federated vs. Local Users</AbsoluteZIndexHeader>

          {loading ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "Password",
                      value: users.filter((u: any) => !u.federated).length,
                    },
                    {
                      name: "Federated",
                      value: users.filter((u: any) => u.federated).length,
                    },
                  ]}
                  labelLine={false}
                  outerRadius={90}
                  dataKey="value"
                  stroke={apptheme.colors.textColorOpposite}
                  strokeWidth={5}
                  label={renderCustomizedLabel}
                >
                  <Cell fill="#8884d8" />
                  <Cell fill="#82ca9d" />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <ConversationAnalyticsTab />
      </Grid>

      {/* User Table */}
      <Grid item xs={12}>
        <Users />
      </Grid>
    </Grid>
  );
};

const fetchNewUsersPerDay = (users) => {
  const newUsersPerDay: any = [];

  // Get the current date and calculate the date one month ago
  const currentDate = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(currentDate.getMonth() - 1);

  // Loop through each API key to gather active users by day
  users.forEach((key) => {
    const createdDate = new Date(key.createdAt);
    const owner = key._id;

    // Only process keys created within the last month
    if (createdDate >= oneMonthAgo) {
      // Format the date as "YYYY-MM-DD"
      const dateString = createdDate.toISOString().split("T")[0];

      // Find index of this date in our array
      const existingIndex = newUsersPerDay.findIndex(
        (data) => data.date === dateString
      );

      if (existingIndex === -1) {
        // If no data for this day, create a new entry with a new Set
        newUsersPerDay.push({
          date: dateString,
          newResources: new Set([owner]),
        });
      } else {
        // Update the existing entry by adding this user to the set
        newUsersPerDay[existingIndex].newResources.add(owner);
      }
    }
  });

  // Convert Sets to arrays for proper serialization before logging or returning
  const result = newUsersPerDay.map((item) => ({
    date: item.date,
    count: item.newResources.size,
    users: Array.from(item.newResources),
  }));

  return result;
};

async function fetchUsers() {
  try {
    const response = await getUsers();
    return response;
  } catch (error) {
    console.error("Error fetching users:", error);
  }
}
